/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { PageComponent } from '@pages/Type';
import { Trans } from 'react-i18next';
import classNames from 'classnames';
import { LinkAuthenticated } from '@root/components/HeaderContext';
import styles from './PricesSection.module.scss';
import { OrderPlan } from './Index.page';
import { useRef } from 'react';
import useHover from '@react-hook/hover';

type PricesSectionProps = {
    noData?: boolean;
    plans: OrderPlan[];
};

type PricesSectionItemProps = {
    plan: OrderPlan;
};

const PricesSectionItem: PageComponent<PricesSectionItemProps> = ({ plan }) => {
    const target = useRef(null);
    const targetTooltip = useRef(null);
    const isHovering = useHover(target, { enterDelay: 50, leaveDelay: 200 });
    const isHoveringTooltip = useHover(targetTooltip, {
        enterDelay: 50,
        leaveDelay: 200,
    });
    return (
        <div className="col-sm-12 col-lg-4" key={`vps-pricing-${plan.id}`}>
            <div className={classNames(styles.prices_inner)}>
                {plan.prices.priceOld > 0 && (
                    <span className={classNames(styles.discount)}>
                        <span className={styles.up_to}>
                            <Trans i18nKey="upTo" ns="landingServicesVps">
                                iki
                            </Trans>
                        </span>
                        -{plan.prices.savePercentage}%
                    </span>
                )}
                <h3>{plan.name}</h3>
                <h2>
                    {plan.prices.price.toFixed(2)} €/
                    <small>
                        <Trans i18nKey="perMonth" ns="landingServicesVps">
                            mėn.
                        </Trans>
                    </small>
                </h2>
                {plan.prices.priceOld > 0 && (
                    <h4>
                        {plan.prices.priceOld.toFixed(2)} €/
                        <small>
                            <Trans i18nKey="perMonth" ns="landingServicesVps">
                                mėn.
                            </Trans>
                        </small>
                    </h4>
                )}
                <dl className={`row ${styles.resources}`}>
                    <dt className="col-6">
                        <Trans i18nKey="resource.vcpu" ns="landingServicesVps">
                            vCPU
                        </Trans>
                    </dt>
                    <dd
                        className={`col-6 ${styles.vcup} ${styles.explain_container}`}
                        ref={target}
                    >
                        <span className={`${styles.explain_text}`}>
                            {plan.resources.cpu}
                        </span>
                        <div
                            className={styles.explain_tooltip}
                            style={{
                                display:
                                    isHovering || isHoveringTooltip
                                        ? 'block'
                                        : 'none',
                            }}
                            ref={targetTooltip}
                        >
                            <Trans
                                i18nKey="vcpuTooltip"
                                ns="landingServicesVps"
                            >
                                Paaiskinimas vCPU
                            </Trans>
                        </div>
                    </dd>

                    <dt className="col-6">
                        <Trans i18nKey="resource.ram" ns="landingServicesVps">
                            RAM
                        </Trans>
                    </dt>
                    <dd className="col-6">{plan.resources.ram}</dd>

                    <dt className="col-6">
                        <Trans i18nKey="resource.ssd" ns="landingServicesVps">
                            SSD
                        </Trans>
                    </dt>
                    <dd className="col-6">{plan.resources.ssd}</dd>

                    <dt className="col-6">
                        <Trans
                            i18nKey="resource.bandwidth"
                            ns="landingServicesVps"
                        >
                            Pralaidumas
                        </Trans>
                    </dt>
                    <dd className="col-6">{plan.resources.bandwidth}</dd>

                    <dt className="col-6">
                        <Trans
                            i18nKey="resource.traffic"
                            ns="landingServicesVps"
                        >
                            Srautas
                        </Trans>
                    </dt>
                    <dd className="col-6">{plan.resources.traffic}</dd>

                    <dt className="col-6">
                        <Trans
                            i18nKey="resource.backup"
                            ns="landingServicesVps"
                        >
                            Rezervines kopijos
                        </Trans>
                    </dt>
                    <dd className="col-6">
                        <Trans
                            i18nKey="resource.included"
                            ns="landingServicesVps"
                        >
                            Iskaiciuota
                        </Trans>
                    </dd>
                </dl>
                <LinkAuthenticated
                    href="ui_order"
                    params={{
                        type: 'vps',
                        plan: plan.name,
                    }}
                    passHref
                >
                    <a className={`btn btn-primary ${styles.btn}`}>
                        <Trans i18nKey="orderButton" ns="landingServicesVps">
                            Užsakyti
                        </Trans>
                    </a>
                </LinkAuthenticated>
            </div>
        </div>
    );
};

const PricesSection: PageComponent<PricesSectionProps> = ({
    noData,
    plans,
}) => {
    let content = (
        <Trans i18nKey="failedLoadData" ns="landingServicesVps">
            Nepavyko užkrauti informacijos
        </Trans>
    );
    if (!noData) {
        content = (
            <>
                {plans.map((plan) => {
                    return (
                        <PricesSectionItem
                            plan={plan}
                            key={`vps-pricing-${plan.id}`}
                        />
                    );
                })}
            </>
        );
    }

    return (
        <section className={`container ${styles.section}`}>
            <h1 className={styles.title}>
                <Trans i18nKey="prices.title" ns="landingServicesVps">
                    Planai ir kainos
                </Trans>
            </h1>
            <p className={styles.desc}>
                <Trans i18nKey="prices.description" ns="landingServicesVps">
                    VDS - aukštesnės klasės nei VPS (OpenVZ) virtualizacijos
                    technologija (neapsigaukite, tai nėra tas pats kas VPS,
                    tačiau yra tiekėjų, kurie tyčia klaidina šiais pavadinimais,
                    tad būkite atidūs), kuri leidžia į VDS įdiegti bet kokią
                    norimą operacinę sistemą.
                </Trans>
            </p>
            <div className={`row ${styles.prices_container}`}>{content}</div>
            <p className={styles.price_explain}>
                <Trans i18nKey="prices.explain" ns="landingServicesVps">
                    Kainos nurodytos be PVM.
                </Trans>
            </p>
        </section>
    );
};

export default PricesSection;
